"use client";

import React, { useEffect, useState } from "react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { X, Calendar, Shield, Award, Layers, Mail, UserCheck, UserX, Clock, ClipboardList, CheckCircle } from "lucide-react";
import { getChapterDisplayName, formatJourney } from "@/components/dashboard/panels/GigsPanel";

interface Assignment {
    id: string;
    posting_title: string;
    status: string;
    assigned_at: string | null;
    completed_at: string | null;
    recognition_tier: string;
}

interface Application {
    id: string;
    posting_title: string;
    status: string;
    applied_at: string | null;
    note: string | null;
}

interface PoolMembership {
    id: string;
    pool_name: string;
    status: string;
    opted_in_at: string | null;
}

interface ActivityLog {
    id: string;
    gig_type_name: string;
    source_type: string;
    recognition_tier: string | null;
    occurred_at: string | null;
    public_surfaced: boolean;
}

interface VolunteerData {
    volunteer_id: string;
    name: string;
    email: string;
    membership_tier: string | null;
    is_active: boolean;
    chapter: string | null;
    community: string | null;
    nearest_community: string | null;
    journey_stage: string | null;
    profile_photo_url: string | null;
    assignments: Assignment[];
    applications: Application[];
    pools: PoolMembership[];
    activity_logs: ActivityLog[];
}

interface ConsoleVolunteerViewProps {
    volunteerId: string;
    lens: string;
    onClose: () => void;
}

export default function ConsoleVolunteerView({ volunteerId, lens, onClose }: ConsoleVolunteerViewProps) {
    const [data, setData] = useState<VolunteerData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"assignments" | "applications" | "pools" | "logs">("assignments");

    useEffect(() => {
        let isMounted = true;
        async function fetchDetails() {
            setIsLoading(true);
            setError(null);
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/volunteers/${volunteerId}?lens=${lens}`);
                if (!res.ok) {
                    const errBody = await res.json().catch(() => ({}));
                    throw new Error(errBody.detail || "Failed to load volunteer engagement profile.");
                }
                const result = await res.json();
                if (isMounted) {
                    setData(result);
                }
            } catch (err: any) {
                if (isMounted) {
                    setError(err.message || "An unexpected error occurred.");
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        fetchDetails();
        return () => {
            isMounted = false;
        };
    }, [volunteerId, lens]);

    const formatDate = (dStr: string | null, includeTime = false) => {
        if (!dStr) return "—";
        const options: Intl.DateTimeFormatOptions = includeTime 
            ? { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }
            : { month: "short", day: "numeric", year: "numeric" };
        return new Date(dStr).toLocaleDateString("en-US", options);
    };

    const getStatusStyle = (status: string) => {
        const s = status.toLowerCase();
        if (s === "active" || s === "completed" || s === "approved" || s === "open") {
            return "bg-emerald-50 text-emerald-700 border-emerald-100";
        }
        if (s === "pending" || s === "submitted" || s === "pending_staff_review") {
            return "bg-amber-50 text-amber-700 border-amber-100";
        }
        if (s === "rejected" || s === "expired" || s === "cancelled") {
            return "bg-red-50 text-red-700 border-red-100";
        }
        return "bg-gray-50 text-gray-600 border-gray-200";
    };

    const getRecognitionStyle = (tier: string | null) => {
        if (!tier) return "text-gray-400";
        const t = tier.toLowerCase();
        if (t === "tier_a" || t === "tiera") return "text-amber-600 font-bold";
        if (t === "tier_b" || t === "tierb") return "text-[#007a8a] font-bold";
        return "text-[#003d45]";
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#00282f]/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-150">
                {/* Header */}
                <div className="bg-[#004d57] text-white p-6 relative flex items-center justify-between shrink-0">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h2 className="text-xl font-bold uppercase tracking-wider">Volunteer Lens Profile</h2>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border border-white/20 bg-white/10 text-white/90`}>
                                Security: {lens.replace(/_/g, " ")}
                            </span>
                        </div>
                        <p className="text-white/80 text-xs mt-1">Platform metrics and historical volunteer dossier</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all focus:outline-none"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Main Body */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                    {/* Sidebar / Profile overview */}
                    <div className="w-full md:w-80 border-b md:border-b-0 md:border-r border-gray-100 bg-gray-50/50 p-6 flex flex-col gap-6 overflow-y-auto shrink-0">
                        {isLoading ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-12 gap-2">
                                <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                                <p className="text-[9px] font-black tracking-widest text-gray-400 uppercase">Loading details...</p>
                            </div>
                        ) : error || !data ? (
                            <div className="text-center py-12">
                                <UserX className="w-10 h-10 text-red-400 mx-auto mb-2" />
                                <p className="text-xs text-red-600 font-bold">{error || "Volunteer dossier unavailable."}</p>
                            </div>
                        ) : (
                            <>
                                {/* Profile Badge */}
                                <div className="text-center">
                                    {data.profile_photo_url ? (
                                        <img 
                                            src={data.profile_photo_url} 
                                            alt={data.name} 
                                            className="w-16 h-16 rounded-full object-cover mx-auto mb-3 border border-[#007a8a]/20 shadow-sm"
                                        />
                                    ) : (
                                        <div className="w-16 h-16 rounded-full bg-[#007a8a]/10 text-[#007a8a] flex items-center justify-center font-black text-2xl mx-auto mb-3 border border-[#007a8a]/20">
                                            {data.name.charAt(0) || "V"}
                                        </div>
                                    )}
                                    <h3 className="text-base font-black text-[#003d45]">{data.name}</h3>
                                    <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-1">
                                        <Mail className="w-3.5 h-3.5" />
                                        <span>{data.email}</span>
                                    </div>
                                </div>

                                {/* Membership Details */}
                                <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm space-y-4 text-xs">
                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">Account Status</div>
                                        <div className="flex items-center gap-1.5 mt-1 font-bold text-[#003d45]">
                                            {data.is_active ? (
                                                <>
                                                    <UserCheck className="w-4 h-4 text-emerald-500" />
                                                    <span className="text-emerald-700">Active Member</span>
                                                </>
                                            ) : (
                                                <>
                                                    <UserX className="w-4 h-4 text-gray-400" />
                                                    <span className="text-gray-500">Inactive</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">Membership Journey</div>
                                        <div className="font-bold text-[#003d45] mt-1">{formatJourney(data.membership_tier) || "Charter Member"}</div>
                                    </div>

                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">Chapter</div>
                                        <div className="font-bold text-[#003d45] mt-1">{getChapterDisplayName(data.chapter) || "Not Assigned"}</div>
                                    </div>

                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">Community</div>
                                        <div className="font-bold text-[#003d45] mt-1">
                                            {!data.community || data.community.toLowerCase() === "unassigned" 
                                                 ? "Unassigned" 
                                                 : (data.community.toLowerCase().includes("community") ? data.community : `${data.community} Community`)}
                                            {data.nearest_community && 
                                             data.nearest_community.toLowerCase().trim() !== (data.community || "").toLowerCase().trim() && (
                                                <span className="text-gray-400 text-[10px] block font-normal mt-0.5">
                                                    (Nearest: {data.nearest_community.toLowerCase().includes("community") ? data.nearest_community : `${data.nearest_community} Community`})
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">Candidate Journey</div>
                                        <div className="font-bold text-[#003d45] mt-1">{formatJourney(data.journey_stage) || "Not Enrolled"}</div>
                                    </div>
                                </div>

                                {/* Platform Summary */}
                                <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm space-y-3 text-xs">
                                    <h4 className="font-bold text-[#003d45] border-b pb-1.5 border-gray-100">platform engagement</h4>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Active Gigs</span>
                                        <span className="font-black text-[#007a8a]">
                                            {data.assignments.filter(a => a.status.toLowerCase() === "active").length}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Completed Gigs</span>
                                        <span className="font-black text-[#007a8a]">
                                            {data.assignments.filter(a => a.status.toLowerCase() === "completed").length}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Pending Apps</span>
                                        <span className="font-black text-amber-600">
                                            {data.applications.filter(a => a.status.toLowerCase() === "submitted").length}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Standing Pools</span>
                                        <span className="font-black text-[#003d45]">
                                            {data.pools.filter(p => p.status.toLowerCase() === "active").length}
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Tabs & Content area */}
                    <div className="flex-1 overflow-hidden flex flex-col p-6">
                        {isLoading ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                                <div className="w-8 h-8 border-3 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Loading historical data...</p>
                            </div>
                        ) : error || !data ? (
                            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                                Select a valid volunteer profile to view logs and records.
                            </div>
                        ) : (
                            <>
                                {/* Tabs */}
                                <div className="flex gap-2 border-b border-gray-100 pb-px mb-6 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
                                    <button
                                        onClick={() => setActiveTab("assignments")}
                                        className={`pb-3 px-1 text-xs font-black uppercase tracking-wider border-b-2 transition-all shrink-0 ${
                                            activeTab === "assignments" 
                                                ? "border-[#007a8a] text-[#007a8a]" 
                                                : "border-transparent text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        Assignments ({data.assignments.length})
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("applications")}
                                        className={`pb-3 px-1 text-xs font-black uppercase tracking-wider border-b-2 transition-all shrink-0 ${
                                            activeTab === "applications" 
                                                ? "border-[#007a8a] text-[#007a8a]" 
                                                : "border-transparent text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        Applications ({data.applications.length})
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("pools")}
                                        className={`pb-3 px-1 text-xs font-black uppercase tracking-wider border-b-2 transition-all shrink-0 ${
                                            activeTab === "pools" 
                                                ? "border-[#007a8a] text-[#007a8a]" 
                                                : "border-transparent text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        Standing Pools ({data.pools.length})
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("logs")}
                                        className={`pb-3 px-1 text-xs font-black uppercase tracking-wider border-b-2 transition-all shrink-0 ${
                                            activeTab === "logs" 
                                                ? "border-[#007a8a] text-[#007a8a]" 
                                                : "border-transparent text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        Contribution Logs ({data.activity_logs.length})
                                    </button>
                                </div>

                                {/* Content Grid */}
                                <div className="flex-1 overflow-y-auto">
                                    {/* Tab 1: Assignments */}
                                    {activeTab === "assignments" && (
                                        <div className="space-y-3">
                                            {data.assignments.length === 0 ? (
                                                <div className="text-center py-12 text-gray-400 text-xs">
                                                    No gig assignments found for this volunteer.
                                                </div>
                                            ) : (
                                                data.assignments.map(a => (
                                                    <div key={a.id} className="p-4 rounded-2xl border border-gray-100 bg-white shadow-sm flex items-center justify-between gap-4">
                                                        <div className="space-y-1">
                                                            <div className="font-bold text-sm text-[#003d45]">{a.posting_title}</div>
                                                            <div className="flex items-center gap-4 text-[10px] text-gray-400">
                                                                <span className="flex items-center gap-1">
                                                                    <Clock className="w-3.5 h-3.5" />
                                                                    Assigned: {formatDate(a.assigned_at)}
                                                                </span>
                                                                {a.completed_at && (
                                                                    <span className="flex items-center gap-1">
                                                                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                                                        Completed: {formatDate(a.completed_at)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3 shrink-0">
                                                            {a.recognition_tier !== "none" && (
                                                                <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-black tracking-widest ${getRecognitionStyle(a.recognition_tier)}`}>
                                                                    <Award className="w-3.5 h-3.5" />
                                                                    {a.recognition_tier}
                                                                </span>
                                                            )}
                                                            <span className={`inline-block px-2.5 py-1 text-[9px] font-black uppercase tracking-wider border rounded-full ${getStatusStyle(a.status)}`}>
                                                                {a.status.replace(/_/g, " ")}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    {/* Tab 2: Applications */}
                                    {activeTab === "applications" && (
                                        <div className="space-y-3">
                                            {data.applications.length === 0 ? (
                                                <div className="text-center py-12 text-gray-400 text-xs">
                                                    No applications found for this volunteer.
                                                </div>
                                            ) : (
                                                data.applications.map(app => (
                                                    <div key={app.id} className="p-4 rounded-2xl border border-gray-100 bg-white shadow-sm space-y-2">
                                                        <div className="flex justify-between items-start gap-4">
                                                            <div>
                                                                <div className="font-bold text-sm text-[#003d45]">{app.posting_title}</div>
                                                                <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-1">
                                                                    <Calendar className="w-3.5 h-3.5" />
                                                                    Applied: {formatDate(app.applied_at, true)}
                                                                </div>
                                                            </div>
                                                            <span className={`px-2.5 py-1 text-[9px] font-black uppercase tracking-wider border rounded-full ${getStatusStyle(app.status)} shrink-0`}>
                                                                {app.status.replace(/_/g, " ")}
                                                            </span>
                                                        </div>
                                                        <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-3 text-xs italic text-[#555555]">
                                                            {app.note ? `"${app.note}"` : "No statement provided."}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    {/* Tab 3: Standing Pools */}
                                    {activeTab === "pools" && (
                                        <div className="space-y-3">
                                            {data.pools.length === 0 ? (
                                                <div className="text-center py-12 text-gray-400 text-xs">
                                                    No standing pool memberships found.
                                                </div>
                                            ) : (
                                                data.pools.map(pm => (
                                                    <div key={pm.id} className="p-4 rounded-2xl border border-gray-100 bg-white shadow-sm flex items-center justify-between gap-4">
                                                        <div className="space-y-1">
                                                            <div className="font-bold text-sm text-[#003d45]">{pm.pool_name} Standing Pool</div>
                                                            <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                                                <Clock className="w-3.5 h-3.5" />
                                                                Opted-in: {formatDate(pm.opted_in_at)}
                                                            </div>
                                                        </div>
                                                        <span className={`px-2.5 py-1 text-[9px] font-black uppercase tracking-wider border rounded-full ${getStatusStyle(pm.status)} shrink-0`}>
                                                            {pm.status.replace(/_/g, " ")}
                                                        </span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    {/* Tab 4: Contribution Logs */}
                                    {activeTab === "logs" && (
                                        <div className="space-y-3">
                                            {data.activity_logs.length === 0 ? (
                                                <div className="text-center py-12 text-gray-400 text-xs">
                                                    No contribution logs recorded.
                                                </div>
                                            ) : (
                                                <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white">
                                                    <table className="w-full text-left border-collapse">
                                                        <thead>
                                                            <tr className="bg-gray-50/50 border-b border-gray-100 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                                                <th className="py-3 px-4">Event Description</th>
                                                                <th className="py-3 px-4">Activity Source</th>
                                                                <th className="py-3 px-4">Recognition</th>
                                                                <th className="py-3 px-4">Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="text-xs divide-y divide-gray-100 text-gray-600">
                                                            {data.activity_logs.map(log => (
                                                                <tr key={log.id} className="hover:bg-gray-50/20 transition-colors">
                                                                    <td className="py-3 px-4 font-bold text-[#003d45]">{log.gig_type_name}</td>
                                                                    <td className="py-3 px-4 capitalize">{log.source_type.replace(/_/g, " ")}</td>
                                                                    <td className="py-3 px-4 font-bold text-[#007a8a]">
                                                                        {log.recognition_tier && log.recognition_tier !== "none" ? (
                                                                            <span className="flex items-center gap-1">
                                                                                <Award className="w-3.5 h-3.5 shrink-0" />
                                                                                {log.recognition_tier}
                                                                            </span>
                                                                        ) : "—"}
                                                                    </td>
                                                                    <td className="py-3 px-4 text-[10px] text-gray-400">{formatDate(log.occurred_at)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
